import React, { Suspense, lazy, useRef, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import FullPageLoader from "./FullPageLoader";

// Lazy-load page-level components — each page becomes a separate JS chunk
// that is only downloaded when the user visits that route.
const Home = lazy(() => import("./Home"));
const ProductInfo = lazy(() => import("./ProductInfo"));
const Products = lazy(() => import("./Products"));
const TrackOrder = lazy(() => import("./TrackOrder"));
const AdminDashboard = lazy(() => import("./Admin/AdminDashboard"));

// ─── Google Analytics page-view tracker ──────────────────────────────────────
const GA_ID = "G-815TYT9S84";

const usePageTracking = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    window.gtag("config", GA_ID, {
      page_path: location.pathname + location.search,
    });
  }, [location]);
};

const SiteLayout = () => {
  const aboutusRef = useRef(null);
  const contactusRef = useRef(null);

  const scrollToSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Header
        scrollToSection={scrollToSection}
        aboutusRef={aboutusRef}
        contactusRef={contactusRef}
      />
      <Suspense fallback={<FullPageLoader />}>
        <Routes>
          <Route
            path="/"
            element={
              <Home scrollToSection={scrollToSection} aboutusRef={aboutusRef} />
            }
          />
          <Route path="/productinfo/:productId" element={<ProductInfo />} />
          <Route path="/products" element={<Products categoryId={0} />} />
          <Route path="/trackorder" element={<TrackOrder />} />
        </Routes>
      </Suspense>
      <Footer
        scrollToSection={scrollToSection}
        aboutusRef={aboutusRef}
        contactusRef={contactusRef}
      />
    </>
  );
};

// Inner component so useLocation works (must be inside <Router>)
const RoutesWithTracking = () => {
  usePageTracking();
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path="/admin/*" element={<AdminDashboard />} />
        <Route path="/*" element={<SiteLayout />} />
      </Routes>
    </Suspense>
  );
};

const AppRoutes = () => {
  return (
    <Router>
      <RoutesWithTracking />
    </Router>
  );
};

export default AppRoutes;
